import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './ManufacturersList.scss';
import { Breadcrumbs, ErrorMessage, Loader, Page, PageHeader } from 'components';
import { ErrorType, LoadState } from 'enums';
import { ManufacturersListItem } from './ManufacturersListItem';
import { DeviceInfoService, DevicesService } from 'services';
import { useCancelablePromise } from 'hooks';
import { IManufacturer } from 'interfaces';

export function ManufacturersList() {
    const { t, i18n } = useTranslation();
    const { cancelablePromise } = useCancelablePromise();

    const [manufacturers, setManufacturers] = useState<IManufacturer[]>([]);
    const [infoLoadState, setInfoLoadState] = useState<LoadState>(LoadState.Loading);
    const [infoError, setInfoError] = useState('');

    // Load manufacturers the first time we render
    useEffect(() => {
        cancelablePromise(DeviceInfoService.getManufacturerMap())
            .then(() => {
                setManufacturers(DeviceInfoService.allManufacturers);
                setInfoLoadState(LoadState.Success);
            })
            .catch((e) => {
                setInfoLoadState(LoadState.Error);
                setInfoError(e + '')
            });
    }, []);

    const tableHeaders = (
        <div className="manufacturersHeaders">
            <div className="manufacturersHeader manufacturersHeaders_addNew">
                <Link to="/manufacturers/new" title={t('manufacturersList.headers.addNewTooltip')} data-cy="manufacturers_addNewLink">
                    {t('manufacturersList.headers.addNew')}
                    <FontAwesomeIcon icon="plus" className="manufacturersHeaders_addNewIcon" />
                </Link>
            </div>
        </div>
    );

    let content: ReactNode;
    switch (infoLoadState) {
        case LoadState.Loading:
        case LoadState.Success:
            if (manufacturers.length > 0 || infoLoadState === LoadState.Loading) {
                content = (<>
                    <ul className="manufacturersList" data-cy="manufacturers_deviceList">
                        {manufacturers.map(manufacturer => (
                            <ManufacturersListItem manufacturer={manufacturer} key={manufacturer.id} />
                        ))}
                    </ul>
                    {infoLoadState === LoadState.Loading && (
                        <Loader />
                    )}
                </>);
            } else {
                if (DevicesService.filters.length === 0) {
                    content = (
                        <div className="manufacturersList_emptyAddNew">
                            <div className="manufacturersList_addNewArrow">
                                <FontAwesomeIcon icon="reply" />
                            </div>
                            {t('manufacturersList.emptyMessageAddNew')}
                        </div>
                    );
                } 
            }
            break;
        case LoadState.Error:
            content = (
                <ErrorMessage type={ErrorType.Generic} message={infoError || DevicesService.loadErrorMessage} />
            );
            break;
    }

    const breadcrumbs = (
        <Breadcrumbs crumbs={[
            {
                label: t('devicesList.title'),
                url: '/',
            },
        ]} 
        dataCy="edit_breadcrumb"
        />
    );

    return (
        <Page>
            <PageHeader            
                top={breadcrumbs}
                title={t('manufacturersList.title')}
            />
            {tableHeaders}
            {content}
        </Page>
    );
}